import { forwardRef, useEffect, useRef, useState } from 'react';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Input } from './Input';
import type { ControlSize } from './Input';

export interface SearchInputProps {
  /** The committed value. Typing updates the field immediately and `onChange` after `delay`. */
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Debounce in milliseconds. 0 disables debouncing. */
  delay?: number;
  inputSize?: ControlSize;
  /** Required: names the search box for screen readers. */
  label?: string;
  autoFocus?: boolean;
  disabled?: boolean;
  className?: string;
  wrapperClassName?: string;
}

/**
 * Debounced search box. The visible text is local so typing never lags, and the
 * parent only sees a settled value.
 */
export const SearchInput = forwardRef<HTMLInputElement, SearchInputProps>(function SearchInput(
  {
    value,
    onChange,
    placeholder = 'Buscar…',
    delay = 300,
    inputSize = 'md',
    label = 'Buscar',
    autoFocus,
    disabled,
    className,
    wrapperClassName,
  },
  ref,
) {
  const [text, setText] = useState(value);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const lastEmitted = useRef(value);

  // A change coming from the parent (a cleared filter, a URL restore) wins.
  useEffect(() => {
    if (value !== lastEmitted.current) {
      lastEmitted.current = value;
      setText(value);
    }
  }, [value]);

  useEffect(() => {
    if (text === lastEmitted.current) return;
    if (delay <= 0) {
      lastEmitted.current = text;
      onChangeRef.current(text);
      return;
    }
    const id = window.setTimeout(() => {
      lastEmitted.current = text;
      onChangeRef.current(text);
    }, delay);
    return () => window.clearTimeout(id);
  }, [text, delay]);

  const clear = () => {
    setText('');
    lastEmitted.current = '';
    onChange('');
  };

  return (
    <Input
      ref={ref}
      type="search"
      role="searchbox"
      aria-label={label}
      value={text}
      onChange={(event) => setText(event.target.value)}
      placeholder={placeholder}
      icon={Search}
      inputSize={inputSize}
      autoFocus={autoFocus}
      disabled={disabled}
      className={cn('[&::-webkit-search-cancel-button]:hidden', className)}
      wrapperClassName={wrapperClassName}
      trailing={
        text ? (
          <button
            type="button"
            onClick={clear}
            aria-label="Limpar busca"
            className={cn(
              'flex size-9 items-center justify-center rounded-sm text-text-muted',
              'transition-colors duration-150 ease-out-soft hover:bg-surface-3 hover:text-text',
              "relative after:absolute after:left-1/2 after:top-1/2 after:size-11 after:-translate-x-1/2 after:-translate-y-1/2 after:content-['']",
            )}
          >
            <X aria-hidden="true" className="size-4" />
          </button>
        ) : undefined
      }
    />
  );
});
